import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  Form,
  Modal,
  Table,
  Spinner,
  Alert,
} from 'react-bootstrap';
import useSWR from 'swr';
import { medicoAgenda } from '../../../helpers/agenda/medicos';
import api from '../../../services/api';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const formatCurrency = value =>
  (value ?? 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const FaturamentoMedTable = ({ medico, data, totalPorMedico }) => {
  return (
    <div>
      <h5>Médico: {medico.split('-')[1]}</h5>
      <Table bordered hover size="sm">
        <thead>
          <tr>
            <th style={{ width: '30%' }}>Paciente</th>
            <th style={{ width: '30%' }}>Procedimento</th>
            <th style={{ width: '15%' }}>Valor</th>
            <th style={{ width: '15%' }}>Forma de pagamento</th>
            <th style={{ width: '10%' }}>Opções</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(data).map(date => {
            return (
              <React.Fragment key={date}>
                {data[date].map(item => (
                  <Row key={item.CODFATURA} item={item} />
                ))}
                <tr>
                  <td colSpan="3" align="right">
                    <strong>Total:&nbsp;</strong>
                  </td>
                  <td>
                    {formatCurrency(totalPorMedico[medico][date])}
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

const Row = ({ item }) => (
  <tr>
    <td>{item?.NOMEPAC}</td>
    <td>{item?.DESCPROCEDI}</td>
    <td>{formatCurrency(item?.VLRLAUDO)}</td>
    <td>{item.ARQUIVO_URI ?? ''}</td>
    <td>
      <Button
        size="sm"
        onClick={() =>
          window.openEditFatModal(
            item.NOMEPAC,
            item.DESCPROCEDI,
            item.VLRLAUDO,
            item.CODFATURA,
            item.ARQUIVO_URI ?? ''
          )
        }
      >
        Editar
      </Button>
    </td>
  </tr>
);

const medicosSorted = medicoAgenda
  .slice(0, 12)
  .sort((a, b) => a.medico.localeCompare(b.medico));

export default function FormFechamento() {
  const location = useLocation();
  const data = location?.state?.data;
  const {
    data: fechamento,
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR(
    data ? `fechamento/diario?datafatura=${data}` : null,
    url => api.get(url).then(res => res.data),
    { revalidateOnFocus: false }
  );
  const [observacoes, setObservacoes] = useState('');
  const [selectedMedico, setSelectedMedico] = useState([]);
  const [nomePaciente, setNomePaciente] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('');
  const [procedimento, setProcedimento] = useState('');
  const [idFat, setIdFat] = useState();
  const [valorFat, setValorFat] = useState();
  const [editFatModal, setEditFatModal] = useState(false);
  const filteredFechamento = (fechamento ?? []).filter(
    f => selectedMedico.length === 0 || selectedMedico.includes(f.CODMEDICO)
  );

  function agruparDadosPorMedicoEData(dados) {
    const relatorio = {};
    dados.forEach(item => {
      const chaveMedico = `${item.CODMEDICO}-${item.NOMEMED}`;
      const data = new Date(item.DATALAUDO).toLocaleDateString('pt-BR');

      if (!relatorio[chaveMedico]) relatorio[chaveMedico] = {};
      if (!relatorio[chaveMedico][data]) relatorio[chaveMedico][data] = [];
      relatorio[chaveMedico][data].push(item);
    });
    return relatorio;
  }

  function calcularTotalPorMedico(relatorio) {
    const totalPorMedico = {};
    for (const medico in relatorio) {
      totalPorMedico[medico] = {};
      for (const data in relatorio[medico]) {
        totalPorMedico[medico][data] = relatorio[medico][data].reduce(
          (total, item) => total + item.VLRLAUDO,
          0
        );
      }
    }
    return totalPorMedico;
  }

  window.openEditFatModal = (nomePac, procedi, valor, codFat, pag) => {
    setNomePaciente(nomePac);
    setProcedimento(procedi);
    setValorFat(valor);
    setIdFat(codFat);
    setFormaPagamento(pag); // ARQUIVO_URI no backend
    setEditFatModal(true);
  };

  const handleSelectMedico = agenda => {
    setSelectedMedico(prevSelectedMedico => {
      if (prevSelectedMedico.includes(agenda.codigo)) {
        return prevSelectedMedico.filter(codigo => codigo !== agenda.codigo);
      } else {
        return [...prevSelectedMedico, agenda.codigo];
      }
    });
  };

  async function editarFaturamentoMedico(codFatura, valor, pagamento) {
    try {
      const res = await api.put('/faturamento', {
        codFatura,
        valor,
        pagamento,
      });
      mutate(); // revalidate to ensure server state
      toast.success(res?.data?.message);
      setEditFatModal(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || 'Erro ao editar faturamento!'
      );
    }
  }

  const relatorioAgrupado = useMemo(
    () => agruparDadosPorMedicoEData(filteredFechamento),
    [filteredFechamento]
  );

  const totalPorMedico = useMemo(
    () => calcularTotalPorMedico(relatorioAgrupado),
    [relatorioAgrupado]
  );

  const valorTotalGeral = useMemo(() => {
    return Object.values(totalPorMedico).reduce((total, medico) => {
      return (
        total +
        Object.values(medico).reduce((subTotal, valor) => subTotal + valor, 0)
      );
    }, 0);
  }, [totalPorMedico]);

  const generatePDF = (relatorioAgrupado, totalPorMedico, valorTotalGeral) => {
    const tableBody = [];

    Object.keys(relatorioAgrupado).forEach(medico => {
      tableBody.push([
        {
          text: `Médico: ${medico.split('-')[1]}`,
          colSpan: 4,
          alignment: 'center',
          bold: true,
          fontSize: 9,
        },
        {},
        {},
        {},
      ]);

      Object.keys(relatorioAgrupado[medico]).forEach(dataKey => {
        relatorioAgrupado[medico][dataKey].forEach(item => {
          tableBody.push([
            { text: item.NOMEPAC || '', fontSize: 9 },
            { text: item.DESCPROCEDI || '', fontSize: 9 },
            { text: formatCurrency(item.VLRLAUDO), fontSize: 9 },
            { text: item.ARQUIVO_URI ?? '', fontSize: 9 },
          ]);
        });
      });

      tableBody.push([
        {
          text: `Total: ${formatCurrency(
            Object.values(totalPorMedico[medico])[0]
          )}`,
          colSpan: 4,
          alignment: 'right',
          bold: true,
          fontSize: 9,
        },
        {},
        {},
        {},
      ]);
    });

    const medicosFiltro = medicosSorted
      .map(agenda => {
        const marcado = selectedMedico.includes(agenda.codigo) ? '[X]' : '[ ]';
        return `${marcado} ${agenda.medico}`;
      })
      .join('   ');

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 40, 40, 40],
      content: [
        {
          text: `FECHAMENTO DIÁRIO - ${fechamento?.[0]?.dataFormatada || ''}`,
          fontSize: 16,
          bold: true,
          margin: [0, 0, 0, 12],
        },
        {
          text: medicosFiltro,
          fontSize: 9,
          margin: [0, 0, 0, 12],
        },
        {
          table: {
            widths: ['*', '*', 70, 80],
            body:
              tableBody.length > 0
                ? tableBody
                : [
                  [
                    {
                      text: 'Nenhum registro para o filtro selecionado.',
                      colSpan: 4,
                      alignment: 'center',
                      fontSize: 9,
                    },
                    {},
                    {},
                    {},
                  ],
                ],
          },
          layout: 'grid',
        },
        {
          text: `Valor total geral: ${formatCurrency(valorTotalGeral)}`,
          fontSize: 12,
          margin: [0, 12, 0, 16],
        },
        {
          text: 'Observações:',
          bold: true,
          fontSize: 11,
          margin: [0, 0, 0, 4],
        },
        {
          table: {
            widths: ['*'],
            heights: [90],
            body: [
              [
                {
                  text: observacoes || ' ',
                  fontSize: 10,
                  margin: [4, 4, 4, 4],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
          },
        },
      ],
    };

    pdfMake.createPdf(docDefinition).open();
  };

  if (error) {
    return (
      <Card id="cardHeader" style={{ marginTop: 10 }}>
        <Card.Header className="text-center font-weight-bold">
          Fechamento diário
        </Card.Header>
        <Card.Body className="text-center">
          <Alert variant="danger" role="alert" aria-live="assertive">
            Falha ao carregar dados.
            <br />
            <Button onClick={() => mutate()} className="mt-2" variant="warning">
              Tentar novamente
            </Button>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (isLoading || isValidating) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 160 }}
      >
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <>
      <Card id="cardHeader" style={{ marginTop: 10 }}>
        <Card.Header className="text-center font-weight-bold">
          FECHAMENTO DIÁRIO - {fechamento?.[0]?.dataFormatada}
        </Card.Header>
        <Card.Body className="text-center">
          {medicosSorted.map(agenda => (
            <Form.Check
              key={agenda.codigo}
              inline
              checked={selectedMedico.includes(agenda.codigo)}
              label={agenda.medico}
              size="sm"
              onClick={() => handleSelectMedico(agenda)}
            />
          ))}
        </Card.Body>
        <div className="d-grid text-center">
          <Button onClick={() => setSelectedMedico([])}>Limpar Filtros</Button>{' '}
          <Button
            variant="warning"
            onClick={() =>
              generatePDF(relatorioAgrupado, totalPorMedico, valorTotalGeral)
            }
          >
            Imprimir
          </Button>
        </div>
        {Object.keys(relatorioAgrupado).map(medico => (
          <FaturamentoMedTable
            key={medico}
            medico={medico}
            data={relatorioAgrupado[medico]}
            totalPorMedico={totalPorMedico}
          />
        ))}
        <div style={{ textAlign: 'center', marginTop: 5 }}>
          <strong>
            Total geral:{' '}
            {formatCurrency(valorTotalGeral)}
          </strong>
        </div>
        <strong>Observações</strong>
        <Form.Control
          as="textarea"
          rows={2}
          className="mb-3"
          value={observacoes}
          onChange={e => setObservacoes(e.target.value)}
        />
      </Card>

      <Modal show={editFatModal}>
        <Modal.Header>
          <Modal.Title>Editar faturamento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>Nome do paciente:</strong> {nomePaciente}
          </p>
          <p>
            <strong>Procedimento:</strong> {procedimento}
          </p>
          <strong>Valor: </strong>
          <input
            type="number"
            value={valorFat}
            onChange={e => setValorFat(parseFloat(e.target.value))}
          />
          <div style={{ marginTop: 10 }}>
            <strong>Forma de pagamento: </strong>
            <input
              type="text"
              value={formaPagamento}
              onChange={e => setFormaPagamento(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditFatModal(false)}>
            Fechar
          </Button>
          <Button
            onClick={() =>
              editarFaturamentoMedico(idFat, valorFat, formaPagamento)
            }
          >
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
